import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { usePostHog } from "@posthog/react";
import { useMemo } from "react";
import { THROW_REACTION_MENU } from "../reactionThrowAssets";
import { PlayerInfo, TeamId } from "../interface/GameTypes";

/** Lowest seat on the trailing team — server requires a concrete `toPlayerUUID` for throws. */
export function pickDefaultTrailingTargetUuid(players: PlayerInfo[], trailingTeam: TeamId): string | null {
  const sorted = players
    .filter((p) => p.teamId === trailingTeam && p.uuid)
    .sort((a, b) => a.seatIndex - b.seatIndex);
  return sorted[0]?.uuid ?? null;
}

interface Props {
  trailingTeam: TeamId;
  players: PlayerInfo[];
  cooldownMs: number;
  onPickReaction: (reactionId: string) => void;
}

export function DealIntroWaitingTauntPanel({ trailingTeam, players, cooldownMs, onPickReaction }: Props) {
  const posthog = usePostHog();
  const compact = useMediaQuery("(max-height: 520px)");
  const narrow = useMediaQuery("(max-width: 420px)");
  const disabled = cooldownMs > 0;

  const targetUuid = useMemo(
    () => pickDefaultTrailingTargetUuid(players, trailingTeam),
    [players, trailingTeam]
  );
  const targetName = useMemo(() => {
    const t = players.find((p) => p.uuid === targetUuid);
    if (!t) return "trailing team";
    return t.name?.trim() || `Seat ${t.seatIndex}`;
  }, [players, targetUuid]);

  const canSend = Boolean(targetUuid);

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.82rem", lineHeight: 1.45, mb: 1 }}>
        Taunt the trailing team — everyone at the table sees the throw.{" "}
        {canSend ? (
          <>Pick any throw below.</>
        ) : (
          <strong>No trailing player found — reactions unavailable.</strong>
        )}
      </Typography>
      <Box
        sx={
          compact || narrow
            ? {
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 0.75,
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorX: "contain",
                pb: 0.5,
                mx: -0.5,
                px: 0.5,
              }
            : {
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 0.75,
              }
        }
      >
        {THROW_REACTION_MENU.map((r) => (
          <Button
            key={r.id}
            variant="contained"
            disabled={disabled || !canSend}
            title={r.label}
            onClick={() => {
              posthog?.capture("reaction_sent", {
                reaction_id: r.id,
                reaction_label: r.label,
                target_name: targetName,
                context: "deal_intro_waiting_strip",
              });
              onPickReaction(r.id);
            }}
            sx={
              compact || narrow
                ? {
                    flex: "0 0 auto",
                    minWidth: 88,
                    maxWidth: 108,
                    py: 0.65,
                    px: 0.4,
                    fontWeight: "bold",
                    fontSize: "0.68rem",
                    lineHeight: 1.15,
                    textTransform: "none",
                    whiteSpace: "normal",
                    background: r.color,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.2,
                    "&:hover": { background: r.color, filter: "brightness(1.08)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                  }
                : {
                    py: 0.85,
                    px: 0.5,
                    fontWeight: "bold",
                    fontSize: "0.78rem",
                    textTransform: "none",
                    lineHeight: 1.15,
                    background: r.color,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.2,
                    "&:hover": { background: r.color, filter: "brightness(1.08)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                  }
            }
          >
            <Box component="span" sx={{ fontSize: compact || narrow ? "1.2rem" : "1.05rem", lineHeight: 1 }}>
              {r.emoji}
            </Box>
            <Box component="span">{r.label}</Box>
          </Button>
        ))}
      </Box>
      {disabled && (
        <Typography sx={{ fontSize: "0.72rem", color: "#ffc107", textAlign: "center", mt: 0.75 }}>
          Cooldown: {Math.ceil(cooldownMs / 1000)}s
        </Typography>
      )}
    </Box>
  );
}
